import React, { useRef, useState } from 'react';
import { Wallet, X } from 'lucide-react';
import useDialog from '../../hooks/useDialog';
import { cashDrawerAPI } from '../../services/api';
import { cleanDecimal } from '../invoices/InvoiceEditors';
import { formatPaisa, fromPaisa, toPaisa } from '../../utils/money';

// Why the question is being asked, in the cashier's terms.
const PROMPT = {
  cash: 'This takes or hands back cash, so your drawer has to be open first. Count the cash already in it and enter the amount.',
  counter: 'Every payment taken at this counter goes into your drawer, so open it before you start billing. Count the cash you are starting with and enter the amount.',
  refused: 'Your drawer is not open, so the cash could not be recorded. Open it to carry on.',
  manual: 'Count the cash already in the drawer and enter the amount.',
};

// What opening a drawer means, for staff who read it more easily in Urdu.
const URDU_NOTE =
  'دراز کھولنے کا مطلب یہ ہے کہ اس وقت آپ کے پاس جو نقد رقم موجود ہے، وہ درج کر دی جائے۔ '
  + 'اس کے بعد ہر نقد وصولی اور ہر واپسی آپ کی دراز میں درج ہوتی رہے گی۔ '
  + 'شفٹ کے آخر میں آپ دراز کی نقد رقم گن کر بند کریں گے، اور کمی یا زیادتی کی وجہ لکھنی ہوگی۔';

/** Opens the cashier's drawer with the cash already in it. */
const OpenDrawerDialog = ({ reason = 'manual', onOpened, onCancel }) => {
  const [typed, setTyped] = useState('');
  const [focused, setFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const cancel = () => { if (!saving) onCancel(); };
  const dialogRef = useDialog(cancel, { confirmDiscard: false, initialFocus: inputRef });

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (typed === '' || typed === '.') {
      setError('Enter the starting cash. Enter 0 if the drawer is empty.');
      inputRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onOpened(await cashDrawerAPI.open(fromPaisa(toPaisa(typed))));
    } catch (err) {
      const data = err?.data || err?.body || {};
      // Opened a moment ago in another tab: that drawer is the one to use.
      if (data.code === 'DRAWER_ALREADY_OPEN' && data.drawer) {
        onOpened(data.drawer);
        return;
      }
      setError(err?.message || 'The drawer could not be opened. Try again.');
      setSaving(false);
    }
  };

  const display = focused || typed === '' ? typed : formatPaisa(toPaisa(typed));

  return (
    <div
      ref={dialogRef}
      aria-labelledby="open-drawer-title"
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4"
    >
      <form onSubmit={submit} noValidate className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Wallet className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <h2 id="open-drawer-title" className="text-lg font-bold text-white">Open your cash drawer</h2>
          </div>
          <button type="button" aria-label="Close" onClick={cancel} className="p-2 hover:bg-white/20 rounded-xl">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">{PROMPT[reason] || PROMPT.manual}</p>
          <p className="urdu-text rounded-xl bg-emerald-50 px-3 py-2 text-sm leading-loose text-emerald-900" dir="rtl">
            {URDU_NOTE}
          </p>
          <div>
            <label htmlFor="drawer-opening-float" className="block text-xs font-semibold text-gray-600 mb-1">
              Starting cash <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center rounded-xl border-2 border-gray-200 px-3 focus-within:border-emerald-500">
              <span className="text-sm font-semibold text-gray-500" aria-hidden="true">Rs</span>
              <input
                id="drawer-opening-float"
                ref={inputRef}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0"
                value={display}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onChange={(e) => {
                  setTyped(cleanDecimal(e.target.value));
                  if (error) setError(null);
                }}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? 'drawer-opening-error' : undefined}
                className="w-full px-2 py-2 text-xl font-bold text-gray-900 tabular-nums focus:outline-none"
              />
            </div>
            {error && <p id="drawer-opening-error" role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-3">
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-60"
          >
            {saving ? 'Opening...' : 'Open drawer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OpenDrawerDialog;
